import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/news';
import { formatViews, getCategoryById } from '@/utils/newsUtils';

interface NewsCardProps {
  article: NewsArticle;
  layout?: 'row' | 'column';
  showSummary?: boolean;
}

export default function NewsCard({ article, layout = 'row', showSummary = true }: NewsCardProps) {
  const category = getCategoryById(article.categoryId);

  const meta = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
      <span className="flex items-center gap-1 whitespace-nowrap">
        <i className="ri-time-line"></i>
        {article.publishedAt}
      </span>
      <span className="flex items-center gap-1 whitespace-nowrap">
        <i className="ri-eye-line"></i>
        {formatViews(article.views)}
      </span>
    </div>
  );

  const badge = category && (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700 whitespace-nowrap">
      <i className={category.icon}></i>
      {category.name}
    </span>
  );

  if (layout === 'column') {
    return (
      <article className="group h-full rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-colors hover:border-primary-300">
        <Link to={`/detail/${article.id}`} className="block cursor-pointer">
          <div className="relative w-full aspect-[16/9] overflow-hidden bg-background-100">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 吃瓜网热点新闻`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="p-4">
          <div className="flex items-center gap-2">{badge}</div>
          <h3 className="mt-2.5 text-base font-semibold text-foreground-950 leading-snug line-clamp-2">
            <Link to={`/detail/${article.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {article.title}
            </Link>
          </h3>
          {showSummary && (
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
          )}
          <div className="mt-3">{meta}</div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col sm:flex-row gap-4 rounded-lg border border-background-200 bg-background-50 p-3 sm:p-4 transition-colors hover:border-primary-300">
      <Link to={`/detail/${article.id}`} className="block w-full sm:w-52 shrink-0 cursor-pointer">
        <div className="w-full aspect-[16/10] overflow-hidden rounded-md bg-background-100">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 吃瓜网热点新闻`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="flex items-center gap-2">{badge}</div>
        <h3 className="mt-2 text-base md:text-lg font-semibold text-foreground-950 leading-snug line-clamp-2">
          <Link to={`/detail/${article.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {article.title}
          </Link>
        </h3>
        {showSummary && (
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
        )}
        <div className="mt-3 sm:mt-auto sm:pt-3">{meta}</div>
      </div>
    </article>
  );
}